
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type ModelConfig = Database['public']['Tables']['model_configs']['Row'];

export const useAvailableModels = () => {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActiveModels = async () => {
      try {
        const { data, error } = await supabase
          .from('model_configs')
          .select('*')
          .eq('is_active', true)
          .order('priority', { ascending: true })
          .order('category')
          .order('display_name');

        if (error) {
          console.error('Error fetching models:', error);
          throw error;
        }
        
        setModels(data || []);
      } catch (error) {
        console.error('Error fetching active models:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchActiveModels();

    // Set up real-time subscription with improved error handling
    const channel = supabase
      .channel(`active-models-${Date.now()}-${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'model_configs' },
        (payload) => {
          
          // Handle different types of changes
          if (payload.eventType === 'INSERT') {
            const newModel = payload.new as ModelConfig;
            if (newModel.is_active) {
              setModels(prev => [...prev, newModel].sort((a, b) => {
                // Sort by priority first (lower number = higher priority)
                if (a.priority !== b.priority) {
                  return (a.priority || 100) - (b.priority || 100);
                }
                // Then by category
                if (a.category !== b.category) {
                  return a.category.localeCompare(b.category);
                }
                // Finally by display name
                return a.display_name.localeCompare(b.display_name);
              }));
            }
          } else if (payload.eventType === 'UPDATE') {
            const updatedModel = payload.new as ModelConfig;
            setModels(prev => {
              if (updatedModel.is_active) {
                // Add or update the model
                const exists = prev.find(m => m.id === updatedModel.id);
                if (exists) {
                  return prev.map(m => m.id === updatedModel.id ? updatedModel : m).sort((a, b) => {
                    // Sort by priority first (lower number = higher priority)
                    if (a.priority !== b.priority) {
                      return (a.priority || 100) - (b.priority || 100);
                    }
                    // Then by category
                    if (a.category !== b.category) {
                      return a.category.localeCompare(b.category);
                    }
                    // Finally by display name
                    return a.display_name.localeCompare(b.display_name);
                  });
                } else {
                  return [...prev, updatedModel].sort((a, b) => {
                    // Sort by priority first (lower number = higher priority)
                    if (a.priority !== b.priority) {
                      return (a.priority || 100) - (b.priority || 100);
                    }
                    // Then by category
                    if (a.category !== b.category) {
                      return a.category.localeCompare(b.category);
                    }
                    // Finally by display name
                    return a.display_name.localeCompare(b.display_name);
                  });
                }
              } else {
                // Remove the model if it's no longer active
                return prev.filter(m => m.id !== updatedModel.id);
              }
            });
          } else if (payload.eventType === 'DELETE') {
            const deletedModel = payload.old as ModelConfig;
            setModels(prev => prev.filter(m => m.id !== deletedModel.id));
          }
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn('⚠️ Models real-time subscription failed, falling back to periodic refresh');
          // Fallback to periodic refresh every 5 minutes
          const fallbackInterval = setInterval(fetchActiveModels, 300000);
          return () => clearInterval(fallbackInterval);
        }
      });

    return () => {
      try {
        supabase.removeChannel(channel);
      } catch (error) {
        console.error('Error cleaning up models subscription:', error);
      }
    };
  }, []);

  return { models, loading };
};
